import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import AddContactModal from 'components/modals/contact/AddContactModal';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util';
import HoursDisplay from 'components/util/HoursDisplay';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLazyGetMinContactsWithRelationsListWithFiltersQuery } from 'store/api/contacts';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, ContactMinDtoWithRelationsDto, PartyRelationshipTypeCode, PartyType } from 'types/contacts';
import { EffortForm, NominationLineForm, NominationReferee, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { ExpenseDto, ExpenseFormData } from 'types/expenses';
import { FeatureFlag } from 'types/features';
import { NamedEntityMinDto } from 'types/other';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { joinNames, t } from 'utils/getTranslations';
import { hoursToSeconds } from 'utils/time';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { nominationName } from '../../rankings-referees/nominationName';
import { joinReferees, newNominationLine } from '../effortFormMapping';
import { getMissingEmployersOfPersons } from '../effortFormUtils';

/** A line as its referees' confirmations and labels name it, "Chambers Europe 2027 · Banking & finance". */
export const nominationLineName = (line: NominationLineForm, index: number): string =>
    line.legalDirectory
        ? nominationName(line.legalDirectory.label, line.editionYear, line.practiceArea?.name)
        : t('efforts.nominations.line', { n: String(index + 1) });

interface Props {
    initialData?: EffortForm;
    classifiers: Record<string, ClassifierData>;
    /** The effort's expenses as stored, which the expense list starts from. */
    expenses: ExpenseDto[];
}

/**
 * The effort form's state, shared by the create and edit pages and by the record's sections: the form, the
 * pickers' grouping, the employers prefilled from the people, the used time, the expense list being edited, and
 * what changing the type or the people does to a Rankings referee effort's nominations.
 */
export const useEffortForm = ({ initialData, classifiers, expenses }: Props) => {
    const defaultValues: EffortForm = {
        id: null,
        effortActivityType: null,
        description: '',
        effortDate: formatDateStringInput(new Date()),
        usedTimeInSeconds: null,
        usedTimeInHours: '',
        effortByEmployee: null,
        employees: [],
        matters: [],
        partyPersons: [],
        partyCompanies: [],
        expenses: [],
    };

    const { openModal, closeModal } = useModal();

    const methods = useForm<EffortForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
    });
    const { control, setValue, getValues, formState } = methods;

    // Watch values to dynamically render certain fields
    const [partyCompanies] = useWatch({ control, name: ['partyCompanies'] });
    const [partyPersons] = useWatch({ control, name: ['partyPersons'] });
    const effortActivityType = useWatch({ control, name: 'effortActivityType' });

    // Without the feature a Rankings referee effort is an ordinary one: its nominations stay as they are, unseen.
    const hasRankingsReferees = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE);
    const showsNominations = hasRankingsReferees && effortActivityType?.value === RANKINGS_REFEREE_TYPE;
    // The person whose removal from Effort for people was refused last, for a colleague without the feature.
    const [refereeRefusal, setRefereeRefusal] = useState<string | null>(null);

    const selectedPartyPersonIds = partyPersons.map(({ id }) => id);
    const selectedPartyCompanyIds = partyCompanies.map(({ id }) => id);

    const groupEffortForEntities = useCallback(
        (option: ContactMinDtoWithRelationsDto) => {
            if (!selectedPartyPersonIds.length) {
                return null;
            }
            const relatedParties = (option.relations || [])
                .filter(
                    ({ partyRelationshipTypeCode }) => partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE
                )
                .filter(({ otherParty }) => selectedPartyPersonIds.includes(otherParty.id));
            if (relatedParties.length > 0) {
                const relatedParty = relatedParties
                    .sort((a, b) => {
                        if (a.otherParty.name < b.otherParty.name) {
                            return -1;
                        }
                        if (a.otherParty.name > b.otherParty.name) {
                            return 1;
                        }
                        return 0;
                    })
                    .at(0);
                return relatedParty?.otherParty.name || null;
            }
            return t('field.other');
        },
        [selectedPartyPersonIds]
    );

    const groupEffortForPersons = useCallback(
        (option: ContactMinDtoWithRelationsDto) => {
            if (!selectedPartyCompanyIds.length) {
                return null;
            }
            const relatedParties = (option.relatesTo || [])
                .filter(
                    ({ partyRelationshipTypeCode }) => partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE
                )
                .filter(({ otherParty }) => selectedPartyCompanyIds.includes(otherParty.id));
            if (relatedParties.length > 0) {
                const relatedParty = relatedParties
                    .sort((a, b) => {
                        if (a.otherParty.name < b.otherParty.name) {
                            return -1;
                        }
                        if (a.otherParty.name > b.otherParty.name) {
                            return 1;
                        }
                        return 0;
                    })
                    .at(0);
                return relatedParty?.otherParty.name || null;
            }
            return t('field.other');
        },
        [selectedPartyCompanyIds]
    );

    const [fetchMinContactsWithRelations] = useLazyGetMinContactsWithRelationsListWithFiltersQuery();

    // TODO(react-doctor/no-derived-useState): controlled-uncontrolled hybrid — local list is
    // initialized from the `expenses` prop and reset to it via cancelEditing. Refactor to lift
    // ownership to the parent so this becomes fully controlled.
    const [expenseFormDataList, setExpenseFormDataList] = useState<ExpenseFormData[]>(expenses);

    const readOnlyUsedTimeInHours = useMemo(
        () => <HoursDisplay value={Number(initialData?.usedTimeInHours || 0)} />,
        [initialData]
    );

    const onUsedTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const hoursValue = event.target.value;

            let secondsValue: number | null = null;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }
            if (parseFloat(hoursValue || '0') === parseFloat(initialData?.usedTimeInHours || '0')) {
                setValue('usedTimeInSeconds', initialData?.usedTimeInSeconds || null);
            } else {
                setValue('usedTimeInSeconds', secondsValue);
            }
        },
        [initialData, setValue]
    );

    /**
     * People added to Effort for people join every line, with the status Requested. Only the people added: a
     * referee removed from one line stays off it.
     */
    const joinLines = (added: NamedEntityMinDto[]) => {
        const lines = getValues('nominations');
        if (!showsNominations || !lines?.length || added.length === 0) {
            return;
        }
        setValue('nominations', joinReferees(lines, added), { shouldDirty: true });
    };

    const onAddNewContact = (newContact: ContactDto) => {
        const fieldData = getValues('partyPersons');
        fieldData?.push(newContact);
        setValue('partyPersons', fieldData);
        joinLines([newContact]);
    };

    const openAddNewContactModal = (): void => {
        openModal(
            <AddContactModal
                onContactAdd={onAddNewContact}
                onClose={closeModal}
                contactType={PartyType.PERSON}
                preSelectedEmployer={
                    partyCompanies?.length
                        ? {
                              id: partyCompanies[0].id,
                              name: partyCompanies[0].name,
                              partyType: PartyType.COMPANY,
                          }
                        : undefined
                }
                connectedContactsGroupName={t('contacts.groups.contacts-connected-to-effort')}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const onAddNewCompanyContact = (newContact: ContactDto) => {
        const fieldData = getValues('partyCompanies');
        fieldData?.push(newContact);
        setValue('partyCompanies', fieldData);
    };

    const openAddNewCompanyContactModal = (): void => {
        openModal(
            <AddContactModal
                onContactAdd={(value) => onAddNewCompanyContact(value)}
                onClose={closeModal}
                contactType={PartyType.COMPANY}
                preSelectedEmployee={undefined}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    /** Asks before a destructive change; cancelling it, or closing the modal, runs `onCancel`. */
    const confirmDestructive = (
        copy: { titleId: string; titleValues?: Record<string, string>; text: string; confirmId: string },
        onConfirm: () => void,
        onCancel: () => void = () => undefined
    ) => {
        const cancel = () => {
            onCancel();
            closeModal();
        };
        openModal(
            <ConfirmationModal
                titleId={copy.titleId}
                titleValues={copy.titleValues}
                confirmId={copy.confirmId}
                content={<p className="text-body1">{copy.text}</p>}
                onCancel={cancel}
                onConfirm={() => {
                    onConfirm();
                    closeModal();
                }}
                alert={{ ...copy, description: copy.text, tone: 'danger' }}
            />,
            cancel
        );
    };

    /** Removes a nomination, asking first once its referee has answered: a Requested one goes at once. */
    const confirmRemoveNomination = (
        referee: NominationReferee,
        nomination: string,
        onConfirm: () => void,
        onCancel?: () => void
    ) => {
        if (referee.statusCode === 'REQUESTED') {
            onConfirm();
            return;
        }
        const name = referee.party.name;
        const status = t(`rankings-referees.status.${referee.statusCode}`);
        confirmDestructive(
            {
                titleId: 'efforts.nominations.confirm-remove-title',
                titleValues: { name },
                text: t('efforts.nominations.confirm-remove', { name, status, nomination }),
                confirmId: 'efforts.nominations.confirm-remove-action',
            },
            onConfirm,
            onCancel
        );
    };

    /** A new line holds everyone in Effort for people, for the edition after the effort's year. */
    const newLine = () => newNominationLine(getValues('effortDate'), getValues('partyPersons'));

    /**
     * Removes a line, asking first when one of its referees has answered: about that nomination when it is the only
     * one, about the line's answered referees together otherwise.
     */
    const confirmRemoveLine = (line: NominationLineForm, index: number, onConfirm: () => void) => {
        const answered = line.referees.filter(({ statusCode }) => statusCode !== 'REQUESTED');
        const nomination = nominationLineName(line, index);
        if (answered.length === 0) {
            onConfirm();
            return;
        }
        if (answered.length === 1) {
            confirmRemoveNomination(answered[0], nomination, onConfirm);
            return;
        }
        confirmDestructive(
            {
                titleId: 'efforts.nominations.confirm-remove-line-title',
                titleValues: { nomination },
                text: t('efforts.nominations.confirm-remove-line', {
                    nomination,
                    referees: joinNames(
                        answered.map(
                            ({ party, statusCode }) => `${party.name} (${t(`rankings-referees.status.${statusCode}`)})`
                        )
                    ),
                }),
                confirmId: 'efforts.nominations.confirm-remove-line-action',
            },
            onConfirm
        );
    };

    /**
     * Choosing Rankings referee starts a line of everyone in Effort for people; choosing another type for an effort
     * with lines asks first, then drops them, or puts the type back.
     */
    const onActivityTypeChange = (option: { value: string } | null) => {
        const lines = getValues('nominations');
        if (option?.value === RANKINGS_REFEREE_TYPE) {
            if (hasRankingsReferees && !lines?.length) {
                setValue('nominations', [newLine()]);
            }
            return;
        }
        if (!lines?.length) {
            return;
        }
        const previousType = effortActivityType;
        confirmDestructive(
            {
                titleId: 'efforts.nominations.confirm-type-title',
                text: t('efforts.nominations.confirm-type'),
                confirmId: 'efforts.nominations.confirm-type-action',
            },
            () => setValue('nominations', []),
            () => setValue('effortActivityType', previousType)
        );
    };

    /**
     * A person who joins Effort for people joins every line. A person who leaves it leaves every line, asking first
     * when one of their nominations has an answer. Without the feature a person holding a nomination stays, and the
     * refusal says who.
     */
    const onPartyPersonsChange = (people: NamedEntityMinDto[]) => {
        const kept = new Set(people.map(({ id }) => id));
        const before = new Set(partyPersons.map(({ id }) => id));
        joinLines(people.filter(({ id }) => !before.has(id)));
        const removed = partyPersons.filter(({ id }) => !kept.has(id));
        const locked = removed.filter(({ id }) => getValues('lockedRefereePartyIds')?.includes(id));
        setRefereeRefusal(
            locked.length
                ? t('efforts.nominations.referee-locked', {
                      name: joinNames(locked.map(({ name }) => name)),
                      count: String(locked.length),
                  })
                : null
        );
        if (locked.length) {
            setValue('partyPersons', [...people, ...locked]);
            return;
        }
        const lines = getValues('nominations');
        const dropped = (lines ?? []).flatMap((line, index) =>
            line.referees
                .filter(({ party }) => !kept.has(party.id))
                .map((referee) => ({ referee, nomination: nominationLineName(line, index) }))
        );
        if (!lines || dropped.length === 0) {
            return;
        }
        // One question for the change, about the first answered nomination it removes.
        const answered = dropped.find(({ referee }) => referee.statusCode !== 'REQUESTED') ?? dropped[0];
        confirmRemoveNomination(
            answered.referee,
            answered.nomination,
            () =>
                setValue(
                    'nominations',
                    lines.map((line) => ({
                        ...line,
                        referees: line.referees.filter(({ party }) => kept.has(party.id)),
                    }))
                ),
            () => setValue('partyPersons', [...people, ...removed])
        );
    };

    // A Rankings referee effort is for the companies its referees represent, which the user picks: no employers added.
    const maybePrefillEffortForEntities = useCallback(async () => {
        const effortForPeople = getValues('partyPersons');
        if (effortForPeople.length === 0 || formState.dirtyFields.partyCompanies || showsNominations) {
            return;
        }
        const { data } = await fetchMinContactsWithRelations({
            prioritizeCompaniesOf: effortForPeople.map(({ id }) => id),
            page: 0,
            pageSize: 100,
        });

        // The selection may have changed while the request was in flight; only employers of the people
        // selected now may be added.
        const currentPersonIds = getValues('partyPersons').map(({ id }) => id);
        const effortForEntities = getValues('partyCompanies');
        const missingEmployers = getMissingEmployersOfPersons(data?.content || [], currentPersonIds, effortForEntities);

        if (missingEmployers.length >= 1) {
            setValue('partyCompanies', [...effortForEntities, ...missingEmployers], { shouldDirty: false });
        }
    }, [formState, getValues, setValue, fetchMinContactsWithRelations, showsNominations]);

    return {
        methods,
        classifiers,
        selectedPartyPersonIds,
        selectedPartyCompanyIds,
        groupEffortForEntities,
        groupEffortForPersons,
        maybePrefillEffortForEntities,
        readOnlyUsedTimeInHours,
        onUsedTimeChange,
        openAddNewContactModal,
        openAddNewCompanyContactModal,
        expenseFormDataList,
        setExpenseFormDataList,
        showsNominations,
        hasRankingsReferees,
        refereeRefusal,
        newLine,
        onActivityTypeChange,
        onPartyPersonsChange,
        confirmRemoveNomination,
        confirmRemoveLine,
    };
};

export type EffortFormState = ReturnType<typeof useEffortForm>;
